import classNames from 'classnames'
import {
  CSSProperties,
  ReactNode,
  RefObject,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { IPopupContentData, PortalContext } from 'core-components/library/popup'
import styles from 'core-components/library/popup/AbsolutePopup.module.scss'
import { FIXED_CLASS } from 'core-components/library/popup/constants'
import {
  calculatePosition,
  IPopupData,
} from 'core-components/library/popup/helpers/calculatePosition'
import { getSizes } from 'core-components/library/popup/helpers/getSizes'
import {
  IPosition,
  IPositionStyles,
  TPopupHorizontal,
  TPopupVertical,
} from 'core-components/library/popup/interface/position'

interface IUseRecalculatePosition {
  width?: number
  popupClassName?: string
  verticalDirection: TPopupVertical
  horizontalDirection: TPopupHorizontal
  verticalStartPosition: TPopupVertical
  horizontalStartPosition: TPopupHorizontal
  verticalOffset: number
  horizontalOffset: number
  popupContent: (contentData: IPopupContentData) => ReactNode

  visible: boolean
  popupRef: RefObject<HTMLDivElement | null>
  rootRef: RefObject<HTMLDivElement | null>
  closePopup: () => void
  resizeCountContext: number
}

export const useRecalculatePosition = ({
  width,
  popupClassName,
  visible,
  popupRef,
  rootRef,
  horizontalStartPosition,
  horizontalDirection,
  verticalDirection,
  verticalStartPosition,
  horizontalOffset,
  verticalOffset,
  closePopup,
  popupContent,
  resizeCountContext,
}: IUseRecalculatePosition) => {
  const [position, setPosition] = useState<IPosition>({})

  const { containerParent, popupPositionRoot } = useContext(PortalContext)

  const fixedRef = useRef<boolean | null>(null)
  const isInitialized = !!Object.keys(position).length
  const getPositionType = () => (fixedRef.current ? 'fixed' : 'absolute')

  const popupStyles: CSSProperties = useMemo(() => {
    const properties = ['top', 'left', 'right', 'bottom', 'width', 'height']
    const resultPosition: IPositionStyles = {}

    properties.map((property: string) => {
      if (position[property] !== undefined) {
        resultPosition[property] = `${position[property]}px`
      }
    })
    if (width) {
      resultPosition.width = `${width}px`
    }
    resultPosition.position = getPositionType()
    return resultPosition
  }, [position, width])

  const popupClasses = classNames(
    styles.popupRoot,
    popupClassName,
    isInitialized ? '' : styles.hidden,
    fixedRef.current ? FIXED_CLASS : ''
  )

  const recalculatePosition = () => {
    if (!rootRef.current || !popupRef.current || !visible) {
      return
    }

    if (fixedRef.current === null) {
      const isFixed = rootRef.current.closest(`.${FIXED_CLASS}`)
      fixedRef.current = !!isFixed
    }

    const { targetCoords, popupCoords } = getSizes(popupRef.current, rootRef.current, { width })

    // Check document.body sizes for tests
    const isTargetHidden = targetCoords.width === 0 && containerParent.clientWidth !== 0
    if (isTargetHidden) {
      closePopup()
      return
    }

    const popupData: IPopupData = {
      startPosition: {
        horizontal: horizontalStartPosition,
        vertical: verticalStartPosition,
      },
      direction: {
        horizontal: horizontalDirection,
        vertical: verticalDirection,
      },
      offset: {
        horizontal: horizontalOffset,
        vertical: verticalOffset,
      },
      position: getPositionType(),
      targetCoords,
      popupCoords,
    }
    const isPositioned = (element?: HTMLElement | null): boolean => {
      if (!element) {
        return false
      }
      const { position } = getComputedStyle(element)
      return (
        position === 'relative' ||
        position === 'absolute' ||
        position === 'fixed' ||
        position === 'sticky'
      )
    }

    const effectiveContainer = isPositioned(popupPositionRoot) ? popupPositionRoot : undefined
    const newPosition = calculatePosition(popupData, effectiveContainer)
    if (JSON.stringify(position) !== JSON.stringify(newPosition)) {
      setPosition(newPosition)
    }
  }

  useLayoutEffect(() => {
    const handler = () => {
      recalculatePosition()
    }
    window.addEventListener('resize', handler)
    if (!fixedRef.current) {
      document.addEventListener('scroll', handler, true)
    }
    return () => {
      document.removeEventListener('scroll', handler, true)
      window.removeEventListener('resize', handler)
    }
  })

  useEffect(() => {
    recalculatePosition()
  }, [
    rootRef,
    popupContent,
    visible,
    verticalDirection,
    horizontalDirection,
    verticalStartPosition,
    horizontalStartPosition,
    resizeCountContext,
  ])

  return { position, popupClasses, popupStyles }
}
